/**
 * Utility for detecting partial placements in AutoPark results
 *
 * A "partial placement" occurs when an aircraft is placed for only PART of its trip duration,
 * leaving gaps where it's assigned to the ramp but has no physical position.
 */

import { Placement, ParkingRequest } from "../../TemporalTest/types";

export interface GapPeriod {
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
}

export interface PartialPlacementInfo {
  aircraftId: string;
  tailNumber: string;
  tripStart: Date;
  tripEnd: Date;
  totalDurationMinutes: number;
  placedDurationMinutes: number;
  gapDurationMinutes: number;
  gapPercentage: number;
  gaps: GapPeriod[];
}

/**
 * Detect partial placements in AutoPark results
 *
 * @param placement - Placement result from algorithm (maps aircraft ID to placement data)
 * @param requests - Parking requests containing trip timing information
 * @param startTime - Start time of the planning horizon
 * @returns Array of partial placement information for aircraft with gaps
 */
export function detectPartialPlacements(
  placement: Placement,
  requests: ParkingRequest[],
  startTime: Date
): PartialPlacementInfo[] {
  const partialPlacements: PartialPlacementInfo[] = [];

  // Convert time bucket to actual date
  const bucketToDate = (bucket: number): Date => {
    const minutes = bucket * 15; // 15-minute buckets
    return new Date(startTime.getTime() + minutes * 60 * 1000);
  };

  // Process each request to check for partial placements
  for (const request of requests) {
    const placementData = placement[request.id];

    // Skip if aircraft wasn't placed at all
    if (!placementData) {
      continue;
    }

    const tripStart = bucketToDate(request.arrivalBucket);
    const tripEnd = bucketToDate(request.departureBucket);
    const totalDurationMinutes = (tripEnd.getTime() - tripStart.getTime()) / (1000 * 60);

    // Collect all placed time periods
    const placedPeriods: Array<{ start: Date; end: Date }> = [];

    if ("spotId" in placementData) {
      // Single-spot placement (no moves)
      placedPeriods.push({
        start: tripStart,
        end: tripEnd,
      });
    } else {
      // Multi-segment placement (with moves)
      for (const segment of placementData.segments) {
        placedPeriods.push({
          start: bucketToDate(segment.startBucket),
          end: bucketToDate(segment.endBucket),
        });
      }
    }

    // Sort placed periods by start time
    placedPeriods.sort((a, b) => a.start.getTime() - b.start.getTime());

    // Calculate total placed duration
    const placedDurationMinutes = placedPeriods.reduce(
      (sum, period) => sum + (period.end.getTime() - period.start.getTime()) / (1000 * 60),
      0
    );

    // Detect gaps in the timeline
    const gaps: GapPeriod[] = [];
    let currentTime = tripStart;

    for (const placedPeriod of placedPeriods) {
      // If there's a gap before this placed period
      if (currentTime < placedPeriod.start) {
        const gapDuration = (placedPeriod.start.getTime() - currentTime.getTime()) / (1000 * 60);
        gaps.push({
          startTime: currentTime,
          endTime: placedPeriod.start,
          durationMinutes: gapDuration,
        });
      }
      currentTime = placedPeriod.end;
    }

    // Check for gap after all placed periods
    if (currentTime < tripEnd) {
      const gapDuration = (tripEnd.getTime() - currentTime.getTime()) / (1000 * 60);
      gaps.push({
        startTime: currentTime,
        endTime: tripEnd,
        durationMinutes: gapDuration,
      });
    }

    // If there are gaps, this is a partial placement
    if (gaps.length > 0) {
      const gapDurationMinutes = gaps.reduce((sum, gap) => sum + gap.durationMinutes, 0);
      const gapPercentage = (gapDurationMinutes / totalDurationMinutes) * 100;

      partialPlacements.push({
        aircraftId: request.id,
        tailNumber: request.tail_number || request.id,
        tripStart,
        tripEnd,
        totalDurationMinutes,
        placedDurationMinutes,
        gapDurationMinutes,
        gapPercentage,
        gaps,
      });
    }
  }

  return partialPlacements;
}

/**
 * Format duration in minutes to human-readable string
 * @param minutes - Duration in minutes
 * @returns Formatted string like "2d 5h 30m" or "5h 30m" or "30m"
 */
export function formatDuration(minutes: number): string {
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = Math.floor(minutes % 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);

  return parts.length > 0 ? parts.join(" ") : "0m";
}

/**
 * Format time range for display
 * @param startTime - Start of the period
 * @param endTime - End of the period
 * @returns Formatted string like "May 22 at 10:30 AM - May 23 at 9:00 AM"
 */
export function formatTimeRange(startTime: Date, endTime: Date): string {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const startDate = formatDate(startTime);
  const endDate = formatDate(endTime);
  const startTimeStr = formatTime(startTime);
  const endTimeStr = formatTime(endTime);

  if (startDate === endDate) {
    return `${startDate} ${startTimeStr} - ${endTimeStr}`;
  } else {
    return `${startDate} at ${startTimeStr} - ${endDate} at ${endTimeStr}`;
  }
}
